import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import {
  TEXT_BLOCK_MODAL_OPEN_EVENT,
  TEXT_BLOCK_MODAL_CLOSE_EVENT,
  getTextBlockModalContext,
  closeTextBlockModal
} from '../utils/textBlockModalBus';
import { getPdfViewerOpen } from '../utils/pdfViewerBus';
import { supabase } from '../lib/supabase_client';

export const TextBlockModal: React.FC = () => {
  const modalRootRef = useRef<HTMLDivElement>(null);
  const modalBackdropRef = useRef<HTMLDivElement>(null);
  const modalPanelRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const applyPdfDrawerLayout = useCallback(() => {
    if (!modalRootRef.current || !modalBackdropRef.current || !modalPanelRef.current) return;
    if (modalRootRef.current.style.display !== 'flex') return;
    if (!getPdfViewerOpen()) return;

    const wrap = document.querySelector('.main-content-wrap.with-pdf-viewer') as HTMLElement | null;
    const rect = wrap?.getBoundingClientRect();
    const wrapLeft = rect ? rect.left : 0;
    const wrapWidth = rect ? rect.width : window.innerWidth;
    const leftPaneWidth = Math.round(wrapWidth * 0.6);
    const leftPaneRight = wrapLeft + leftPaneWidth;
    const rightGap = Math.max(0, Math.round(window.innerWidth - leftPaneRight));

    modalRootRef.current.style.pointerEvents = 'none';
    modalRootRef.current.style.alignItems = 'flex-start';
    modalRootRef.current.style.justifyContent = 'flex-start';
    modalRootRef.current.style.padding = '0';

    modalBackdropRef.current.style.pointerEvents = 'auto';
    modalBackdropRef.current.style.left = '0px';
    modalBackdropRef.current.style.right = `${rightGap}px`;
    modalBackdropRef.current.style.top = '0';
    modalBackdropRef.current.style.bottom = '0';
    modalBackdropRef.current.style.background = 'rgba(0,0,0,0.15)';

    modalPanelRef.current.style.pointerEvents = 'auto';
    modalPanelRef.current.style.position = 'fixed';
    modalPanelRef.current.style.left = `${Math.round(wrapLeft) + 16}px`;
    modalPanelRef.current.style.top = '16px';
    modalPanelRef.current.style.margin = '0';
    modalPanelRef.current.style.width = `${Math.max(420, leftPaneWidth - 32)}px`;
    modalPanelRef.current.style.maxWidth = 'none';
    modalPanelRef.current.style.height = 'calc(100vh - 32px)';
  }, []);

  const scheduleDrawerLayout = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      applyPdfDrawerLayout();
    });
  }, [applyPdfDrawerLayout]);

  // Modal is always mounted but hidden by default (NO parent re-renders)
  useEffect(() => {
    if (modalRootRef.current) {
      modalRootRef.current.style.display = 'none';
    }
  }, []);

  const headerTitleRef = useRef<HTMLHeadingElement>(null);
  const headerSubtitleRef = useRef<HTMLParagraphElement>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [initAttempts, setInitAttempts] = useState(0);
  const [showError, setShowError] = useState(false);
  const [shouldMountEditor, setShouldMountEditor] = useState(false); // mount TinyMCE only when modal is first opened (avoid hidden-init bugs)
  const [editorKey, setEditorKey] = useState(0); // only used for retry (re-mount editor)

  const editorRef = useRef<any>(null);
  const contextRef = useRef<ReturnType<typeof getTextBlockModalContext> | null>(null);
  const initialValueRef = useRef<string>('');
  const contentRef = useRef<string>('');

  // AI table restructure — DOM-controlled, mirroring the EditFilings save
  // button (saveStateBus pattern). This modal hosts TinyMCE: ANY useState here
  // re-renders the whole editor tree, so busy spinner, status message and the
  // preview overlay are all driven via refs with ZERO React state.
  const aiBusyRef = useRef(false);
  const aiPreviewHtmlRef = useRef<string | null>(null);
  const aiButtonRef = useRef<HTMLButtonElement>(null);
  const aiBtnIdleRef = useRef<HTMLSpanElement>(null);
  const aiBtnBusyRef = useRef<HTMLSpanElement>(null);
  const aiMessageRef = useRef<HTMLSpanElement>(null);
  const aiPreviewRootRef = useRef<HTMLDivElement>(null);
  const aiPreviewContentRef = useRef<HTMLDivElement>(null);

  const setAiBusyDom = useCallback((busy: boolean) => {
    aiBusyRef.current = busy;
    if (aiButtonRef.current) aiButtonRef.current.disabled = busy || !editorRef.current;
    if (aiBtnIdleRef.current) aiBtnIdleRef.current.style.display = busy ? 'none' : 'inline-flex';
    if (aiBtnBusyRef.current) aiBtnBusyRef.current.style.display = busy ? 'inline-flex' : 'none';
  }, []);

  const setAiMessageDom = useCallback((msg: string | null) => {
    const el = aiMessageRef.current;
    if (!el) return;
    el.textContent = msg || '';
    el.title = msg || '';
    el.style.display = msg ? 'inline' : 'none';
  }, []);

  const showAiPreview = useCallback((html: string) => {
    aiPreviewHtmlRef.current = html;
    if (aiPreviewContentRef.current) aiPreviewContentRef.current.innerHTML = html;
    if (aiPreviewRootRef.current) aiPreviewRootRef.current.style.display = 'flex';
  }, []);

  const hideAiPreview = useCallback(() => {
    aiPreviewHtmlRef.current = null;
    if (aiPreviewRootRef.current) aiPreviewRootRef.current.style.display = 'none';
    if (aiPreviewContentRef.current) aiPreviewContentRef.current.innerHTML = '';
  }, []);

  const open = useCallback(() => {
    const ctx = getTextBlockModalContext();
    if (!ctx) return;

    contextRef.current = ctx;
    initialValueRef.current = ctx.initialValue || '';
    contentRef.current = initialValueRef.current;

    // Update title immediately (DOM, no props)
    if (headerTitleRef.current) {
      headerTitleRef.current.textContent = 'Edit Text';
    }
    if (headerSubtitleRef.current) {
      headerSubtitleRef.current.textContent = ctx.fieldLabel;
    }

    // Show modal first (so TinyMCE initializes visible)
    if (modalRootRef.current) {
      modalRootRef.current.style.display = 'flex';
    }

    const pdfOpen = getPdfViewerOpen();
    if (modalRootRef.current && modalBackdropRef.current && modalPanelRef.current) {
      if (pdfOpen) {
        // Drawer mode anchored to the ACTUAL split area (after sidebar collapse)
        // Also reflows on zoom/resize via listeners below
        scheduleDrawerLayout();
      } else {
        // Centered modal mode (blocks background like before)
        modalRootRef.current.style.pointerEvents = 'auto';
        modalRootRef.current.style.alignItems = '';
        modalRootRef.current.style.justifyContent = '';
        modalRootRef.current.style.padding = '';

        modalBackdropRef.current.style.pointerEvents = '';
        modalBackdropRef.current.style.left = '0';
        modalBackdropRef.current.style.right = '0';
        modalBackdropRef.current.style.top = '0';
        modalBackdropRef.current.style.bottom = '0';
        modalBackdropRef.current.style.background = '';

        modalPanelRef.current.style.pointerEvents = '';
        modalPanelRef.current.style.position = '';
        modalPanelRef.current.style.left = '';
        modalPanelRef.current.style.top = '';
        modalPanelRef.current.style.width = '';
        modalPanelRef.current.style.maxWidth = '';
        modalPanelRef.current.style.height = '';
        modalPanelRef.current.style.margin = '';

        document.body.style.overflow = 'hidden';
      }
    } else {
      document.body.style.overflow = pdfOpen ? '' : 'hidden';
    }

    // Ensure editor is mounted
    if (!shouldMountEditor) setShouldMountEditor(true);

    // Reset UI flags
    setShowError(false);
    setIsEditorReady(!!editorRef.current);
    setAiBusyDom(false);
    hideAiPreview();
    setAiMessageDom(null);

    // If editor already exists, just set content (no re-init)
    if (editorRef.current && typeof editorRef.current.setContent === 'function') {
      try {
        editorRef.current.setContent(initialValueRef.current);
        // ⚠️ CRITICAL: the editor instance is REUSED across different fields.
        // setContent() does not clear the undo stack, so without this reset,
        // Ctrl+Z in (say) the Director Report would restore the PREVIOUS
        // field's content (e.g. the Auditor Report). Clear history and start
        // a fresh baseline so undo can never cross field boundaries.
        editorRef.current.undoManager.clear();
        editorRef.current.undoManager.add(); // baseline = this field's initial content
        editorRef.current.setDirty(false);
      } catch (e) {
        // If setContent fails, we can still retry via button
        console.error('TinyMCE setContent error:', e);
      }
    }
  }, [shouldMountEditor, setAiBusyDom, hideAiPreview, setAiMessageDom]);

  // Keep drawer proportional on zoom/resize while open in PDF mode
  useEffect(() => {
    const onResize = () => scheduleDrawerLayout();
    window.addEventListener('resize', onResize);
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    vv?.addEventListener('resize', onResize);
    vv?.addEventListener('scroll', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      vv?.removeEventListener('resize', onResize);
      vv?.removeEventListener('scroll', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [scheduleDrawerLayout]);

  const close = useCallback(() => {
    if (modalRootRef.current) modalRootRef.current.style.display = 'none';
    document.body.style.overflow = '';

    // Clear AI UI; a still-running poll loop aborts itself once contextRef is null
    hideAiPreview();
    setAiMessageDom(null);

    const ctx = contextRef.current;
    contextRef.current = null;

    if (ctx?.onAfterClose) {
      try {
        ctx.onAfterClose();
      } catch (e) {
        console.error('TextBlockModal onAfterClose error:', e);
      }
    }
  }, [hideAiPreview, setAiMessageDom]);

  // Listen for open/close events (NO STATE in parent)
  useEffect(() => {
    const onOpen = () => open();
    const onClose = () => close();

    document.addEventListener(TEXT_BLOCK_MODAL_OPEN_EVENT, onOpen);
    document.addEventListener(TEXT_BLOCK_MODAL_CLOSE_EVENT, onClose);

    return () => {
      document.removeEventListener(TEXT_BLOCK_MODAL_OPEN_EVENT, onOpen);
      document.removeEventListener(TEXT_BLOCK_MODAL_CLOSE_EVENT, onClose);
    };
  }, [open, close]);

  // Watchdog: if the TinyMCE script never loads (CDN/host blocked, offline, slow
  // network), onInit never fires and the spinner would run forever. After a
  // timeout, surface the Retry UI instead.
  useEffect(() => {
    if (!shouldMountEditor || showError || isEditorReady) return;
    const timer = setTimeout(() => {
      if (!editorRef.current) {
        console.error('TinyMCE: editor failed to initialize within timeout (script blocked or slow network).');
        setIsEditorReady(false);
        setShowError(true);
      }
    }, 15000);
    return () => clearTimeout(timer);
  }, [shouldMountEditor, editorKey, showError, isEditorReady]);

  const handleSave = () => {
    const ctx = contextRef.current;
    if (ctx?.onSave) {
      ctx.onSave(contentRef.current);
    }
    closeTextBlockModal();
  };

  const handleCancel = () => {
    // Restore to initial content (in editor + ref)
    contentRef.current = initialValueRef.current;
    if (editorRef.current && typeof editorRef.current.setContent === 'function') {
      try {
        editorRef.current.setContent(initialValueRef.current);
      } catch {}
    }
    closeTextBlockModal();
  };
  const handleInitError = () => {
    setIsEditorReady(false);
    
    if (initAttempts < 3) {
      setShowError(true);
    } else {
      setShowError(true);
      // Max attempts reached
    }
  };
  
  const handleRetry = () => {
    setShowError(false);
    setIsEditorReady(false);

    // Clear stale editor reference so the watchdog can detect a failed re-mount
    editorRef.current = null;

    // Force re-mount editor (more reliable than destroy())
    setEditorKey(prev => prev + 1);
    setTimeout(() => setInitAttempts(prev => prev + 1), 200);
  };

  // ── AI table restructure (async job) ─────────────────────────────────────
  // The edge function starts a Lambda job and returns { job_id } immediately
  // (a long note can take minutes — far beyond what an open HTTP connection
  // survives). We then poll ai_restructure_jobs until done/failed, up to 10
  // minutes. Result is shown in a preview overlay; nothing touches the editor
  // until the user explicitly clicks Apply.
  const handleAiRestructure = async () => {
    if (!editorRef.current || aiBusyRef.current) return;
    setAiBusyDom(true);
    setAiMessageDom(null);
    try {
      const currentHtml = editorRef.current.getContent();
      const { data, error } = await supabase.functions.invoke('ai-restructure-table', {
        body: { html_content: currentHtml },
      });
      if (error) throw new Error(error.message || 'Request failed');
      if (!data?.ok || !data?.job_id) throw new Error(data?.error || 'Failed to start AI job');

      // Poll the job row (RLS: user can read own jobs) every 5s, max 10 min
      const deadline = Date.now() + 10 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        if (!contextRef.current) return; // modal was closed — stop polling silently
        const { data: job, error: jobError } = await supabase
          .from('ai_restructure_jobs')
          .select('status, result, error')
          .eq('id', data.job_id)
          .single();
        if (jobError) continue; // transient read error — keep polling

        if (job?.status === 'done') {
          const res = job.result as { changed?: boolean; restructured_html?: string; message?: string } | null;
          if (res?.changed && res.restructured_html) {
            showAiPreview(res.restructured_html);
          } else {
            setAiMessageDom(res?.message || 'No broken table structure detected.');
          }
          return;
        }
        if (job?.status === 'failed') {
          throw new Error(job.error || 'AI restructure failed');
        }
      }
      throw new Error('AI restructure timed out after 10 minutes. Please try again.');
    } catch (e: any) {
      console.error('AI restructure error:', e);
      setAiMessageDom(e.message || 'AI restructure failed. Please try again.');
    } finally {
      setAiBusyDom(false);
    }
  };

  const handleAiApply = () => {
    const html = aiPreviewHtmlRef.current;
    if (!html || !editorRef.current) return;
    // setContent inside an undo transaction → Ctrl+Z restores the original
    editorRef.current.undoManager.transact(() => {
      editorRef.current.setContent(html);
    });
    contentRef.current = html;
    hideAiPreview();
    setAiMessageDom(null);
  };

  const handleAiDiscard = () => {
    hideAiPreview();
  };

  return (
    <>
      {/* Global style to fix TinyMCE dropdown z-index */}
      <style>{`
        /* All TinyMCE floating UI elements */
        .tox.tox-tinymce-aux,
        .tox-tinymce-aux,
        .tox-dialog-wrap,
        .tox-dialog,
        .tox-pop,
        .tox-pop__dialog,
        .tox-menu,
        .tox-toolbar__overflow,
        .tox-collection,
        .tox-swatches,
        .tox-swatch,
        .tox-color-picker-container,
        .tox-form__group,
        /* Color input popup */
        div[role="dialog"][class*="tox"],
        div[aria-label*="swatch"],
        div[class*="tox-"][class*="pop"] {
          z-index: 10002 !important;
        }
        /* Prevent toolbar text buttons clipping descenders (g, y, p) e.g. "Remove grid" */
        .text-block-modal-content .tox .tox-tbtn {
          height: 32px !important;
          line-height: 1.35 !important;
        }
        .text-block-modal-content .tox .tox-tbtn--select,
        .text-block-modal-content .tox .tox-tbtn[aria-label*="Remove grid"] {
          height: 32px !important;
          overflow: visible !important;
        }
        .text-block-modal-content .tox .tox-tbtn__select-label,
        .text-block-modal-content .tox .tox-tbtn > span {
          line-height: 1.35 !important;
          overflow: visible !important;
          max-height: none !important;
        }
      `}</style>
      
      <div 
        ref={modalRootRef}
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4 text-block-modal-backdrop"
        style={{ zIndex: 10000 }}
        onClick={(e) => e.target === e.currentTarget && handleCancel()}
      >
        <div
          ref={modalBackdropRef}
          className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
          onClick={handleCancel}
        />
        <div 
          ref={modalPanelRef}
          className="relative bg-white rounded-xl overflow-hidden shadow-2xl w-full max-w-[90vw] mx-4 text-block-modal-content ring-1 ring-black/10"
          style={{ maxHeight: '95vh', display: 'flex', flexDirection: 'column', zIndex: 10001, pointerEvents: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
          <div>
            <h3 ref={headerTitleRef} className="text-base font-semibold text-[var(--theme-400)]">Edit Text</h3>
            <p ref={headerSubtitleRef} className="mt-0.5 text-xs text-slate-500" />
            {!isEditorReady && (
              <p className="mt-0.5 text-xs text-slate-500">
                <span className="inline-block animate-spin mr-2">⏳</span>
                Initializing editor...
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
            aria-label="Close"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        {/* Modal Body - TinyMCE Editor */}
        <div className="flex-1 overflow-auto p-3" style={{ minHeight: 0 }}>
          {showError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-red-800 font-semibold mb-2">Editor Failed to Load</p>
              <p className="text-red-600 text-sm mb-4">
                {initAttempts < 3 
                  ? `This might be due to network issues. Attempt ${initAttempts}/3.`
                  : 'Editor failed after multiple attempts. Please refresh the page and try again.'}
              </p>
              {initAttempts < 3 && (
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          
          {!showError && (
            shouldMountEditor ? (
            <Editor
            key={editorKey}
            tinymceScriptSrc={`${process.env.PUBLIC_URL || ''}/tinymce/tinymce.min.js`}
            licenseKey="gpl"
            onEditorChange={(newContent) => {
              contentRef.current = newContent;
            }}
            onInit={(evt, editor) => {
              try {
                editorRef.current = editor;
                
                // Verify editor is working
                if (!editor || typeof editor.setContent !== 'function') {
                  throw new Error('Editor not properly initialized');
                }

                // CRITICAL: Defer content setting to next tick to avoid race with open() callback
                setTimeout(() => {
                  try {
                    const nextContent = initialValueRef.current || '';
                    editor.setContent(nextContent);
                    // Fresh undo baseline for this field (undo must never reach
                    // content from a previously edited field — see open())
                    editor.undoManager.clear();
                    editor.undoManager.add();
                    editor.setDirty(false);
                    console.log('TinyMCE: Content set (' + nextContent.length + ' chars)');
                  } catch (e) {
                    console.error('TinyMCE: Failed to set content', e);
                  }
                }, 0);
                
                // Auto-fix dragster blockers after initialization
                setTimeout(() => {
                  const blockers = editor.getDoc().querySelectorAll('.ephox-dragster-blocker');
                  blockers.forEach((blocker: any) => blocker.remove());
                }, 100);
                
                setIsEditorReady(true);
                setShowError(false);
                setInitAttempts(prev => prev + 1);
                
              } catch (error) {
                console.error('TinyMCE init error:', error);
                handleInitError();
              }
            }}
            init={{
              height: Math.max(400, window.innerHeight * 0.95 - 170),
              menubar: false,
              browser_spellcheck: true,
              statusbar: false,
              toolbar_mode: 'sliding',
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'help', 'wordcount'
              ],
              // contextmenu: false,
              toolbar: 'bold italic underline superscript | ' +
                'alignleft aligncenter alignright alignjustify | ' +
                'bullist numlist liststartnumber outdent indent | ' +
                'table tablecellunderline tablecelldoubleunderline tableremovegrid tablesplit | code removeformat undo redo',
              extended_valid_elements: 'ol[start|style],ul[style]',
              advlist_bullet_styles: 'disc circle square',
              advlist_number_styles: 'default,lower-alpha,lower-roman,upper-alpha,upper-roman',
              // House grid style — match lambda/ai-table-restructure/table_builder.py
              // table: width 100%; border-collapse; margin 10pt 0; cellspacing 0; cellpadding 5pt
              // cells: border 1px solid #ddd; padding 5pt  (alignment left to user)
              // Pipeline puts styles INLINE on each td — content_style alone is not enough (lost on save).
              content_style: `
                body { 
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; 
                  font-size: 14px; 
                  line-height: 1.6; 
                }
                table { 
                  border-collapse: collapse; 
                  width: 100%; 
                  margin: 10pt 0; 
                }
                table td, table th { 
                  border: 1px solid #ddd; 
                  padding: 5pt; 
                }
                table th { 
                  font-weight: bold; 
                }
                td.subtotal-row, th.subtotal-row { border-bottom: 1.5px solid #000; }
                td.grandtotal-row, th.grandtotal-row { border-bottom: 3px double #000; }
                tr, td, th { height: auto; }
              `,
              table_use_colgroups: false,
              table_sizing_mode: 'relative',
              table_default_attributes: {
                cellpadding: '5pt',
                cellspacing: '0'
              },
              table_default_styles: {
                'width': '100%',
                'border-collapse': 'collapse',
                'margin': '10pt 0'
              },
              table_style_by_css: true,
              table_cell_class_list: [
                { title: 'Default', value: '' }
              ],
              table_row_class_list: [
                { title: 'Default', value: '' }
              ],
              branding: false,
              resize: true,
              elementpath: false,
              placeholder: 'Start writing your content here...',
              code_dialog_height: 400,
              code_dialog_width: 1000,
              
              // Custom setup function (matches 1.0 lines 360-699)
              setup: (editor) => {
                // Register custom icons
                editor.ui.registry.addIcon('bottomborder', '<img src="/images/bottomborder.png" width="16" height="16" alt="Bottom Border">');
                editor.ui.registry.addIcon('doubleborder', '<img src="/images/doubleborder.png" width="16" height="16" alt="Double Border">');
                
                // Single Underline Button (1.0 lines 368-441)
                editor.ui.registry.addButton('tablecellunderline', {
                  icon: 'bottomborder',
                  tooltip: 'Add/Remove single underline (1.5px border)',
                  onAction: () => {
                    editor.undoManager.transact(() => {
                      const selectedCells: HTMLElement[] = editor.dom.select('td[data-mce-selected],th[data-mce-selected]') as HTMLElement[];
                      if (selectedCells.length === 0) {
                        const currentCell = editor.dom.getParent(editor.selection.getNode(), 'td, th') as HTMLElement;
                        if (currentCell) selectedCells.push(currentCell);
                      }
                      
                      if (selectedCells.length > 0) {
                        const anyWithBorder = selectedCells.some((cell) => {
                          const border = editor.dom.getStyle(cell, 'border-bottom');
                          return border && border.includes('1.5px');
                        });
                        
                        selectedCells.forEach((cell) => {
                          if (anyWithBorder) {
                            // Remove: restore normal border
                            editor.dom.setStyle(cell, 'border-bottom', '1px solid #ddd');
                          } else {
                            // Add underline
                            editor.dom.setStyle(cell, 'border-bottom', '1.5px solid #000');
                          }
                        });
                      } else {
                        editor.notificationManager.open({
                          text: 'Please select a table cell first',
                          type: 'warning',
                          timeout: 3000
                        });
                      }
                    });
                  }
                });
                
                // Double Underline Button (1.0 lines 444-516)
                editor.ui.registry.addButton('tablecelldoubleunderline', {
                  icon: 'doubleborder',
                  tooltip: 'Add/Remove double underline (3px double border)',
                  onAction: () => {
                    editor.undoManager.transact(() => {
                      const selectedCells: HTMLElement[] = editor.dom.select('td[data-mce-selected],th[data-mce-selected]') as HTMLElement[];
                      if (selectedCells.length === 0) {
                        const currentCell = editor.dom.getParent(editor.selection.getNode(), 'td, th') as HTMLElement;
                        if (currentCell) selectedCells.push(currentCell);
                      }
                      
                      if (selectedCells.length > 0) {
                        const anyWithDouble = selectedCells.some((cell) => {
                          const border = editor.dom.getStyle(cell, 'border-bottom');
                          return border && border.includes('double');
                        });
                        
                        selectedCells.forEach((cell) => {
                          if (anyWithDouble) {
                            // Remove: restore normal border
                            editor.dom.setStyle(cell, 'border-bottom', '1px solid #ddd');
                          } else {
                            // Add double underline
                            editor.dom.setStyle(cell, 'border-bottom', '3px double #000');
                          }
                        });
                      } else {
                        editor.notificationManager.open({
                          text: 'Please select a table cell first',
                          type: 'warning',
                          timeout: 3000
                        });
                      }
                    });
                  }
                });

                // TinyMCE setStyle() serializes to `border-width: medium medium 1.5px` +
                // `border-style: none none solid`. Browsers hide `medium`/`none`; SSM/Word
                // draws a full cell box. Write longhands onto the style attribute instead.
                const parseInlineStyle = (el: HTMLElement): Record<string, string> => {
                  const out: Record<string, string> = {};
                  (el.getAttribute('style') || '').split(';').forEach((part) => {
                    const idx = part.indexOf(':');
                    if (idx < 0) return;
                    const key = part.slice(0, idx).trim().toLowerCase();
                    const val = part.slice(idx + 1).trim();
                    if (key) out[key] = val;
                  });
                  return out;
                };

                const writeInlineStyle = (el: HTMLElement, styles: Record<string, string>) => {
                  const css = Object.entries(styles)
                    .filter(([, v]) => v != null && String(v).trim() !== '')
                    .map(([k, v]) => `${k}: ${v}`)
                    .join('; ');
                  if (css) el.setAttribute('style', `${css};`);
                  else el.removeAttribute('style');
                  el.removeAttribute('data-mce-style');
                };

                // Clear formatting walks cell contents and leaves the <td>/<th>
                // style attribute alone, so font-weight: bold on the cell survives.
                // Drop that property only — borders, width, and padding stay.
                const cellsInRemoveFormat = (): HTMLElement[] => {
                  const selected = editor.dom.select('td[data-mce-selected],th[data-mce-selected]') as HTMLElement[];
                  if (selected.length > 0) return selected;

                  const rng = editor.selection.getRng();
                  const hit = (editor.dom.select('td,th') as HTMLElement[]).filter((cell) => {
                    try {
                      return rng.intersectsNode(cell);
                    } catch {
                      return false;
                    }
                  });
                  if (hit.length > 0) return hit;

                  const cell = editor.dom.getParent(editor.selection.getNode(), 'td,th') as HTMLElement | null;
                  return cell ? [cell] : [];
                };

                const stripCellFontWeight = () => {
                  cellsInRemoveFormat().forEach((cell) => {
                    const styles = parseInlineStyle(cell);
                    if (!Object.prototype.hasOwnProperty.call(styles, 'font-weight')) return;
                    delete styles['font-weight'];
                    writeInlineStyle(cell, styles);
                  });
                };

                editor.addCommand('RemoveFormat', () => {
                  editor.formatter.remove('removeformat');
                  stripCellFontWeight();
                  contentRef.current = editor.getContent();
                  editor.nodeChanged();
                });

                const stripBorderStyles = (styles: Record<string, string>) => {
                  Object.keys(styles).forEach((k) => {
                    if (k === 'border' || k.startsWith('border-')) delete styles[k];
                  });
                };

                const classifyKeptUnderline = (cell: HTMLElement): 'single' | 'double' | null => {
                  const computed = (editor.dom.getStyle(cell, 'border-bottom') || '').toLowerCase();
                  const inline = (cell.getAttribute('style') || '').toLowerCase();
                  const hay = `${computed} ${inline}`;
                  if (hay.includes('double')) return 'double';
                  if (hay.includes('1.5px') || hay.includes('1.5pt')) return 'single';
                  return null;
                };

                const applyWordSafeCellBorders = (
                  cell: HTMLElement,
                  underline: 'single' | 'double' | null
                ) => {
                  editor.dom.setAttrib(cell, 'border', null);
                  const styles = parseInlineStyle(cell);
                  stripBorderStyles(styles);
                  // Explicit 0 — never `none` / `medium` / `currentcolor` (Word draws those as a box)
                  styles['border-top'] = '0';
                  styles['border-right'] = '0';
                  styles['border-left'] = '0';
                  if (underline === 'single') {
                    styles['border-bottom'] = '1.5px solid #000000';
                  } else if (underline === 'double') {
                    styles['border-bottom'] = '3px double #000000';
                  } else {
                    styles['border-bottom'] = '0';
                  }
                  writeInlineStyle(cell, styles);
                };

                // Remove table grid (#ddd borders) — keeps single/double underlines for totals
                editor.ui.registry.addButton('tableremovegrid', {
                  text: 'Remove grid',
                  tooltip: 'Remove table grid borders (keeps underlines). Uses selected table, or all tables if none selected.',
                  onAction: () => {
                    editor.undoManager.transact(() => {
                      const node = editor.selection.getNode();
                      const currentTable = editor.dom.getParent(node, 'table') as HTMLElement | null;
                      const tables: HTMLElement[] = currentTable
                        ? [currentTable]
                        : (editor.dom.select('table') as HTMLElement[]);

                      if (tables.length === 0) {
                        editor.notificationManager.open({
                          text: 'No table found in this text block',
                          type: 'warning',
                          timeout: 3000
                        });
                        return;
                      }

                      let cellCount = 0;
                      tables.forEach((table) => {
                        editor.dom.setAttrib(table, 'border', null);
                        const tableStyles = parseInlineStyle(table);
                        stripBorderStyles(tableStyles);
                        tableStyles['border'] = '0';
                        if (!tableStyles['border-collapse']) tableStyles['border-collapse'] = 'collapse';
                        writeInlineStyle(table, tableStyles);

                        const cells = editor.dom.select('td, th', table) as HTMLElement[];
                        cells.forEach((cell) => {
                          applyWordSafeCellBorders(cell, classifyKeptUnderline(cell));
                          cellCount += 1;
                        });
                      });

                      editor.notificationManager.open({
                        text: `Removed grid from ${tables.length} table${tables.length === 1 ? '' : 's'} (${cellCount} cells). Underlines kept.`,
                        type: 'success',
                        timeout: 2500
                      });
                    });
                  }
                });

                // Hard split: close the table before this row and open a new one.
                // Same as typing </table><table ...> between two <tr>s — rows move, nothing is copied.
                editor.ui.registry.addButton('tablesplit', {
                  text: 'Split',
                  tooltip: 'Close this table and open a new one from the current row. Rows are not copied.',
                  onAction: () => {
                    const node = editor.selection.getNode();
                    const cell = editor.dom.getParent(node, 'td,th') as HTMLTableCellElement | null;
                    const row = cell ? (editor.dom.getParent(cell, 'tr') as HTMLTableRowElement | null) : null;
                    const table = row ? (editor.dom.getParent(row, 'table') as HTMLTableElement | null) : null;

                    if (!table || !row) {
                      editor.notificationManager.open({
                        text: 'Click the row where the new table should start',
                        type: 'warning',
                        timeout: 3000
                      });
                      return;
                    }

                    const rows = Array.from(table.rows);
                    const splitAt = rows.indexOf(row);
                    if (splitAt <= 0) {
                      editor.notificationManager.open({
                        text: 'Click a row below the first row',
                        type: 'warning',
                        timeout: 3000
                      });
                      return;
                    }

                    editor.undoManager.transact(() => {
                      const doc = editor.getDoc();
                      const newTable = doc.createElement('table');
                      Array.from(table.attributes).forEach((attr) => {
                        if (attr.name === 'id' || attr.name.indexOf('data-mce-') === 0) return;
                        newTable.setAttribute(attr.name, attr.value);
                      });

                      const newBody = doc.createElement('tbody');
                      newTable.appendChild(newBody);
                      rows.slice(splitAt).forEach((moving) => newBody.appendChild(moving));

                      Array.from(table.tBodies).forEach((body) => {
                        if (body.rows.length === 0) body.remove();
                      });

                      table.insertAdjacentElement('afterend', newTable);
                    });

                    contentRef.current = editor.getContent();
                    editor.notificationManager.open({
                      text: 'Table closed. This row starts the new table.',
                      type: 'success',
                      timeout: 2500
                    });
                  }
                });
                
                // List Start Number Button (1.0 lines 519-544)
                editor.ui.registry.addButton('liststartnumber', {
                  text: 'Start#',
                  tooltip: 'Modify starting number of ordered list (e.g., start from ii.)',
                  onAction: () => {
                    const currentNode = editor.selection.getNode();
                    const currentList = editor.dom.getParent(currentNode, 'ol');
                    
                    if (!currentList) {
                      editor.notificationManager.open({
                        text: 'Please place cursor in a numbered list first',
                        type: 'warning',
                        timeout: 3000
                      });
                      return;
                    }
                    
                    const currentStart = currentList.getAttribute('start') || '1';
                    const newStart = prompt('Enter starting number (e.g., 2 for ii, iii, iv...):', currentStart);
                    
                    if (newStart !== null && !isNaN(Number(newStart)) && newStart.trim() !== '') {
                      editor.undoManager.transact(() => {
                        editor.dom.setAttrib(currentList, 'start', newStart.trim());
                      });
                    }
                  }
                });
                
                // Auto-fix dragster blockers (1.0 lines 547-699)
                let isDragging = false;
                let dragTimeout: any;
                
                const autoFixBlockers = () => {
                  if (isDragging) return;
                  
                  try {
                    const blockers = editor.getDoc().querySelectorAll('.ephox-dragster-blocker');
                    if (blockers.length > 0) {
                      blockers.forEach((blocker: any) => blocker.remove());
                      console.log(`Auto-fixed: Removed ${blockers.length} blocking element(s)`);
                    }
                  } catch (e) {
                    // Ignore cleanup errors
                  }
                };
                
                // Track dragging state
                editor.on('mousedown', (e: any) => {
                  const target = e.target;
                  if (target && (target.tagName === 'TD' || target.tagName === 'TH' || 
                                target.closest('table') || target.classList.contains('mce-resize-bar'))) {
                    isDragging = true;
                    
                    if (dragTimeout) clearTimeout(dragTimeout);
                    
                    dragTimeout = setTimeout(() => {
                      isDragging = false;
                      setTimeout(autoFixBlockers, 100);
                    }, 2000);
                  }
                });
                
                editor.on('mouseup', () => {
                  if (dragTimeout) clearTimeout(dragTimeout);
                  
                  setTimeout(() => {
                    isDragging = false;
                    setTimeout(autoFixBlockers, 200);
                  }, 100);
                });
                
                // Auto-fix on focus and click
                editor.on('focus', () => {
                  if (!isDragging) setTimeout(autoFixBlockers, 100);
                });
                
                editor.on('click', () => {
                  if (!isDragging) setTimeout(autoFixBlockers, 50);
                });
                
                // Apply house grid styles (match table_builder.py) — no alignment forcing.
                // Must write INLINE styles on each cell; editor content_style is not persisted in saved HTML.
                const isKeptUnderline = (borderBottom: string) => {
                  if (!borderBottom) return false;
                  const b = borderBottom.toLowerCase();
                  return b.includes('double') || b.includes('1.5px');
                };

                const stripTableHeights = (table: HTMLElement) => {
                  editor.dom.setStyle(table, 'height', '');
                  editor.dom.setAttrib(table, 'height', null);
                  editor.dom.select('tr, td, th', table).forEach((el: HTMLElement) => {
                    editor.dom.setStyle(el, 'height', '');
                    editor.dom.setAttrib(el, 'height', null);
                  });
                };

                const applyHouseCellGrid = (cell: HTMLElement, opts?: { fillMissingOnly?: boolean }) => {
                  const fillMissingOnly = opts?.fillMissingOnly === true;
                  const style = (cell.getAttribute('style') || '').toLowerCase();
                  const hasBorder = /border(?:-top|-right|-bottom|-left)?\s*:/.test(style);
                  const hasPadding = /padding\s*:/.test(style);
                  const bottom = editor.dom.getStyle(cell, 'border-bottom') || '';
                  const keepUnderline = isKeptUnderline(bottom);

                  if (!fillMissingOnly || !hasBorder) {
                    editor.dom.setStyle(cell, 'border', '1px solid #ddd');
                    if (keepUnderline) {
                      editor.dom.setStyle(cell, 'border-bottom', bottom);
                    }
                  }
                  if (!fillMissingOnly || !hasPadding) {
                    editor.dom.setStyle(cell, 'padding', '5pt');
                  }
                  editor.dom.setStyle(cell, 'height', '');
                  editor.dom.setAttrib(cell, 'height', null);
                };

                const applyHouseTableGrid = (table: HTMLElement, opts?: { fillMissingOnly?: boolean }) => {
                  editor.dom.setAttrib(table, 'cellspacing', '0');
                  editor.dom.setAttrib(table, 'cellpadding', '5pt');
                  editor.dom.setStyle(table, 'width', '100%');
                  editor.dom.setStyle(table, 'border-collapse', 'collapse');
                  editor.dom.setStyle(table, 'margin', '10pt 0');
                  stripTableHeights(table);

                  // Drop TinyMCE colgroup — pipeline uses per-cell width styles instead
                  editor.dom.select('colgroup', table).forEach((cg: HTMLElement) => {
                    editor.dom.remove(cg);
                  });

                  const cells = editor.dom.select('td, th', table) as HTMLElement[];
                  cells.forEach((cell) => applyHouseCellGrid(cell, opts));
                };

                // TinyMCE 6+ fires TableModified when a table is created/changed (more reliable than command name alone)
                editor.on('TableModified', (e: any) => {
                  const table = e?.table as HTMLElement | undefined;
                  if (!table) return;
                  // structure change (insert table / row / col) → ensure house grid on new cells
                  if (e.structure) {
                    setTimeout(() => applyHouseTableGrid(table, { fillMissingOnly: true }), 0);
                  }
                  // Always strip height noise TinyMCE injects while editing
                  setTimeout(() => stripTableHeights(table), 0);
                });

                editor.on('NewCell', (e: any) => {
                  const cell = (e?.node || e?.target) as HTMLElement | undefined;
                  if (!cell) return;
                  setTimeout(() => applyHouseCellGrid(cell, { fillMissingOnly: true }), 0);
                });

                editor.on('ObjectResized', (e: any) => {
                  const target = e?.target as HTMLElement | undefined;
                  if (!target) return;
                  const table = (target.tagName === 'TABLE'
                    ? target
                    : editor.dom.getParent(target, 'table')) as HTMLElement | null;
                  if (table) stripTableHeights(table);
                });

                // Remove height styles from tables after operations
                editor.on('ExecCommand', (e: any) => {
                  const tableCommands = ['mceTableDeleteRow', 'mceTableDeleteCol', 'mceTableInsertRow', 
                                        'mceTableInsertCol', 'mceTableMergeCells', 'mceTableSplitCells'];
                  
                  if (tableCommands.includes(e.command)) {
                    setTimeout(() => {
                      editor.dom.select('table').forEach((table: any) => {
                        stripTableHeights(table);
                      });
                    }, 100);
                  }
                  
                  // New table from scratch → full house grid (inline on every cell)
                  if (e.command === 'mceInsertTable') {
                    setTimeout(() => {
                      const tables = editor.dom.select('table') as HTMLElement[];
                      if (tables.length > 0) {
                        applyHouseTableGrid(tables[tables.length - 1]);
                      }
                    }, 50);
                  }
                  
                  // New rows/columns → only fill missing cell grid styles
                  if (['mceTableInsertRow', 'mceTableInsertRowBefore', 'mceTableInsertRowAfter',
                       'mceTableInsertCol', 'mceTableInsertColBefore', 'mceTableInsertColAfter'].includes(e.command)) {
                    setTimeout(() => {
                      const selectedCell = editor.dom.getParent(editor.selection.getNode(), 'td, th') as HTMLElement | null;
                      const table = selectedCell
                        ? (editor.dom.getParent(selectedCell, 'table') as HTMLElement | null)
                        : null;
                      if (table) applyHouseTableGrid(table, { fillMissingOnly: true });
                    }, 50);
                  }
                });
              }
            }}
          />
          ) : (
            <div className="text-sm text-gray-500">Loading editor…</div>
          )
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <button
              type="button"
              ref={aiButtonRef}
              onClick={handleAiRestructure}
              disabled={!isEditorReady}
              className="inline-flex items-center rounded border border-violet-300 bg-violet-50 px-3 py-1.5 text-[13px] font-semibold text-violet-700 shadow-sm transition hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {/* idle / busy faces swapped via DOM (no React state, no re-renders) */}
              <span ref={aiBtnIdleRef} className="items-center gap-1.5" style={{ display: 'inline-flex' }}>
                ✨ AI Fix Table
                <span className="rounded bg-violet-200 px-1 py-px text-[10px] font-bold uppercase tracking-wide text-violet-800">Beta</span>
              </span>
              <span ref={aiBtnBusyRef} className="items-center gap-1.5" style={{ display: 'none' }}>
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-violet-400 border-t-transparent" />
                Restructuring…
              </span>
            </button>
            {/* (i) info tooltip */}
            <span className="group relative inline-flex">
              <span
                className="inline-flex h-[18px] w-[18px] cursor-help items-center justify-center rounded-full border border-slate-300 text-[11px] font-semibold text-slate-500 hover:bg-slate-100"
                aria-label="What does AI Fix Table do?"
              >
                i
              </span>
              <span className="pointer-events-none absolute bottom-full left-0 z-[10005] mb-2 hidden w-64 rounded-lg bg-slate-800 p-2.5 text-xs leading-relaxed text-white shadow-lg group-hover:block">
                Rebuilds broken or misaligned tables in this text block using AI — you preview the result before it is applied.
              </span>
            </span>
            <span ref={aiMessageRef} className="truncate text-xs text-amber-700" style={{ display: 'none' }} />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isEditorReady}
              className="inline-flex items-center justify-center rounded bg-slate-900 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save & Close
            </button>
          </div>
        </div>

        {/* AI restructure preview overlay (covers the editor, inside the panel).
            Always mounted, shown/hidden and filled via refs — no React state. */}
        <div ref={aiPreviewRootRef} className="absolute inset-0 z-[10003] flex-col bg-white" style={{ display: 'none' }}>
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
            <div>
              <h3 className="text-base font-semibold text-violet-700">AI Restructured Preview</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Review the rebuilt table below. Nothing is changed until you click Apply — Ctrl+Z restores the original afterwards.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAiDiscard}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close preview"
            >
              <span className="text-lg leading-none">×</span>
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4" style={{ minHeight: 0 }}>
            {/* innerHTML is set imperatively in showAiPreview() */}
            <div
              ref={aiPreviewContentRef}
              className="text-sm leading-relaxed [&_table]:w-full"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
            />
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
            <button
              type="button"
              onClick={handleAiDiscard}
              className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleAiApply}
              className="inline-flex items-center justify-center rounded bg-violet-600 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-violet-700"
            >
              Apply to Editor
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

